<template>
  <div class="privilege-reports-page">
    <header class="pr-header">
      <h1 class="pr-title">Privilege Reports</h1>
      <p class="pr-lead">
        Exports who can do what in your organization: an All Users tab —
        staff and patients, one row per privilege held, riskiest first, with
        unexpected patient access flagged — and a GroupPrivileges tab showing
        what each group grants. Download JSON or XLSX for compliance review;
        the JSON also carries the full normalized collections. Most privileges
        relate to electronic PHI (ePHI) access.
      </p>
    </header>

    <div class="pr-actions">
      <button type="button" class="btn btn-primary" :disabled="!accountNumber || loading || !canGenerate" @click="fetchReport">
        Generate report
      </button>
      <button
        v-if="reportData"
        type="button"
        class="btn btn-secondary"
        :disabled="loading"
        @click="downloadJson"
      >
        Download JSON
      </button>
      <button
        v-if="reportData"
        type="button"
        class="btn btn-secondary"
        :disabled="loading"
        @click="downloadXls"
      >
        Download XLSX
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import { PRIVILEGES } from "@/constants/privileges";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionSelfStore } from "@/stores/sessionSelf";
import { sessionActorFrom } from "@/utils/sessionIdentity";
import { assertPrivilegeOrAlert } from "@/utils/privilegeGate";
import { buildReportSheets, GROUPS_PARENT_LEGEND } from "@/utils/privilegeReportRows";
import { buildWorkbookSpec } from "@/utils/privilegeReportWorkbook";
import { renderWorkbook } from "@/utils/privilegeReportXlsx";

export default {
  name: "PrivilegeReportsPage",
  data() {
    return {
      loading: false,
      reportData: null,
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    accountNumber() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).accountnumber;
    },
    actorUniqueId() {
      return sessionActorFrom(this.allData, useSessionSelfStore().profile).unique_ID;
    },
    // Dedicated report gate; the server enforces the same privilege — this is
    // a convenience, not the boundary.
    canGenerate() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.GENERATE_PRIVILEGES);
    },
  },
  methods: {
    normalizeApiPayload(data) {
      if (!data || typeof data !== "object") return {};
      if (Object.prototype.hasOwnProperty.call(data, "status") || Object.prototype.hasOwnProperty.call(data, "data")) {
        return data;
      }
      const keys = Object.keys(data);
      if (keys.length === 1 && data[keys[0]] && typeof data[keys[0]] === "object") {
        return data[keys[0]];
      }
      return data;
    },
    async fetchReport() {
      if (!this.accountNumber) return;
      if (!assertPrivilegeOrAlert(PRIVILEGES.GENERATE_PRIVILEGES, "You do not have permission to generate the privilege report.")) {
        return;
      }
      this.loading = true;
      try {
        const content = {
          Privileges: {
            API_Meth: "GetPrivilegeReport",
            accountnumber: this.accountNumber,
            actor_unique_ID: this.actorUniqueId,
          },
        };
        const res = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const payload = this.normalizeApiPayload(res.data);
        const ok = payload && String(payload.status || "").toLowerCase().includes("success");
        if (ok && payload.data) {
          this.reportData = payload.data;
        } else {
          this.reportData = null;
        }
      } catch (e) {
        this.reportData = null;
        console.error("Privilege report generation failed:", e);
      } finally {
        this.loading = false;
      }
    },
    safeAccount() {
      return String(this.accountNumber || "account").replace(/[^\w.-]+/g, "_");
    },
    downloadJson() {
      if (!this.reportData) return;
      // Mirror the workbook: one top-level key per sheet, from the same shaper
      // the XLSX uses, so the two formats cannot drift.
      const sheets = buildReportSheets(this.reportData);
      const envelope = {
        _meta: {
          reportTitle: this.reportData.reportTitle || "PACMNY Privilege Report",
          generatedAt: this.reportData.generatedAt || "",
          organization: this.reportData.organization || null,
          patientExclusion: this.reportData.patientExclusion || null,
          legends: { Groups: GROUPS_PARENT_LEGEND },
        },
      };
      for (const sheet of sheets) {
        envelope[sheet.name] = sheet.rows;
      }
      const json = JSON.stringify(envelope, null, 2);
      const blob = new Blob([json], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `privilege-report-${this.safeAccount()}-${new Date().toISOString().slice(0, 10)}.json`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    async downloadXls() {
      if (!this.reportData) return;
      // Styled workbook (approved preview design): Overview + styled sheets.
      // Content comes from the same shaper as the JSON mirror.
      const workbook = renderWorkbook(buildWorkbookSpec(this.reportData));
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `privilege-report-${this.safeAccount()}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped lang="scss">
$pr-navy: #173a63;
$pr-border: #e2e8f0;
$pr-muted: #64748b;

.privilege-reports-page {
  max-width: 52rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 2.5rem;
}

.pr-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $pr-border;
}

.pr-title {
  margin: 0 0 0.5rem;
  font-size: 1.65rem;
  font-weight: 700;
  color: $pr-navy;
  letter-spacing: -0.02em;
}

.pr-lead {
  margin: 0;
  font-size: 0.95rem;
  color: $pr-muted;
  line-height: 1.5;
}

.pr-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}
</style>
